import { useCallback, useMemo } from 'react'

import ButtonWithTooltip from 'common/components/ButtonWithTooltip'
import { Column } from 'common/components/table'
import BaseCell from 'common/components/table/cells/Base'
import Toggle from 'common/components/toggle'
import Tooltip from 'common/components/Tooltip'
import { Edit as EditIcon, InfoCircle } from 'common/icons'

import { CustomerAttributesResponse } from '../api'
import { CustomerAttributeRecord } from '../api/queries/useGetCustomerAttributeRecordsQuery'

type UseCustomerAttributesColumnsParams = {
  screenWidth: number
  selectedRecord: CustomerAttributeRecord | undefined
  updateAttribute: (params: {
    attributeId: string
    catalogueAttributeId: string
    description: string
    region: string
    isUsed: boolean
    isCmabUsed: boolean
  }) => void
  setAttributeToEdit: (attribute: CustomerAttributesResponse) => void
  setIsEditAttributeModalOpened: (isOpen: boolean) => void
  setDraftDescription: (description: string) => void
}

export const useCustomerAttributesColumns = ({
  screenWidth,
  selectedRecord,
  updateAttribute,
  setAttributeToEdit,
  setIsEditAttributeModalOpened,
  setDraftDescription,
}: UseCustomerAttributesColumnsParams): Column<CustomerAttributesResponse>[] => {
  // Responsive column width configuration
  const columnWidths: Record<string, Record<string, number>> = useMemo(
    () => ({
      attribute: { small: 70, medium: 50, large: 55, xlarge: 120 },
      description: { small: 40, medium: 190, large: 240, xlarge: 450 },
      languageGeneration: { small: 100, medium: 100, large: 100, xlarge: 190 },
      decisioning: { small: 75, medium: 75, large: 75, xlarge: 100 },
      editIcon: { small: 50, medium: 50, large: 50, xlarge: 100 },
    }),
    []
  )

  const getColumnWidth = useCallback(
    (column: string): number => {
      const widths = columnWidths[column]
      if (!widths) {
        return 40
      }

      if (screenWidth < 900) {
        return widths.small
      }
      if (screenWidth < 1200) {
        return widths.medium
      }
      if (screenWidth < 1600) {
        return widths.large
      }
      return widths.xlarge
    },
    [screenWidth, columnWidths]
  )

  return useMemo(
    () => [
      {
        Header: 'Attribute',
        accessor: 'name',
        width: getColumnWidth('attribute'),
        Cell: ({ row }) => {
          return (
            <BaseCell>
              <div className="capitalize-first">{row.original.name}</div>
            </BaseCell>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Description',
        accessor: 'description',
        width: getColumnWidth('description'),
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <BaseCell className="capitalize-first">
              <div className="overflow-hidden whitespace-nowrap overflow-ellipsis">
                {row.original.description}
              </div>
            </BaseCell>
          )
        },
      },
      {
        Header: () => (
          <div className="flex items-center gap-2 whitespace-nowrap">
            Language Generation
            <Tooltip
              overlay={
                <div className="max-w-sm text-sm leading-relaxed">
                  <p className="mb-2">
                    The Language Generation toggle controls whether specific
                    customer attributes are used to automatically create
                    personalised content.
                  </p>
                  <ul className="space-y-1">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>
                        Including an attribute allows our system to use that
                        data point to generate tailored messaging.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>
                        Excluding it ensures that attribute is ignored,
                        resulting in more generic content.
                      </span>
                    </li>
                  </ul>
                </div>
              }
              placement="top"
            >
              <InfoCircle size={4} className="text-coolGray-600 cursor-help" />
            </Tooltip>
          </div>
        ),
        id: 'languageGeneration',
        accessor: 'isUsed',
        disableSortBy: true,
        align: 'center',
        width: getColumnWidth('languageGeneration'),
        Cell: ({ row }) => {
          const isEnabled = row.original.isUsed ?? false

          return (
            <BaseCell>
              <div
                className="flex justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip
                  overlay={
                    isEnabled ? 'Exclude attribute' : 'Include attribute'
                  }
                  placement="top"
                >
                  <Toggle
                    checked={isEnabled}
                    onChange={(checked) => {
                      updateAttribute({
                        attributeId: row.original.id,
                        catalogueAttributeId: selectedRecord?.id ?? '',
                        description: row.original.description,
                        region: selectedRecord?.region ?? '',
                        isUsed: checked,
                        isCmabUsed: row.original.isCmabUsed,
                      })
                    }}
                    label=""
                    disabled={false}
                  />
                </Tooltip>
              </div>
            </BaseCell>
          )
        },
      },
      {
        Header: () => (
          <div className="flex items-center gap-2 whitespace-nowrap">
            Decisioning
            <Tooltip
              overlay={
                <div className="max-w-sm text-sm leading-relaxed">
                  <p className="mb-2">
                    The Decisioning toggle controls whether specific customer
                    attributes are used in decisioning optimization.
                  </p>
                  <ul className="space-y-1">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>
                        Including an attribute allows the system to use that
                        data point for decisioning analysis.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>
                        Excluding it ensures that attribute is not used in
                        decisioning.
                      </span>
                    </li>
                  </ul>
                </div>
              }
              placement="top"
            >
              <InfoCircle size={4} className="text-coolGray-600 cursor-help" />
            </Tooltip>
          </div>
        ),
        id: 'cmab',
        accessor: 'isCmabUsed',
        disableSortBy: true,
        align: 'center',
        width: getColumnWidth('decisioning'),
        Cell: ({ row }) => {
          const isEnabled = row.original.isCmabUsed ?? false

          return (
            <BaseCell>
              <div
                className="flex justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip
                  overlay={
                    isEnabled
                      ? 'Exclude from Decisioning'
                      : 'Include in Decisioning'
                  }
                  placement="top"
                >
                  <Toggle
                    checked={isEnabled}
                    onChange={(checked) => {
                      updateAttribute({
                        attributeId: row.original.id,
                        catalogueAttributeId: selectedRecord?.id ?? '',
                        description: row.original.description,
                        region: selectedRecord?.region ?? '',
                        isUsed: row.original.isUsed,
                        isCmabUsed: checked,
                      })
                    }}
                    label=""
                    disabled={false}
                  />
                </Tooltip>
              </div>
            </BaseCell>
          )
        },
      },
      {
        width: getColumnWidth('editIcon'),
        disableSortBy: true,
        id: 'id',
        accessor: 'id',
        disableFilters: true,
        align: 'center',
        shouldOverflow: true,
        Cell: ({ row }) => {
          return (
            <BaseCell>
              <ButtonWithTooltip
                tooltip
                className="invisible flex-shrink-0 group-hover:visible"
                tooltipText="Update description"
                variant="icon"
                onClick={() => {
                  setAttributeToEdit(row.original)
                  setIsEditAttributeModalOpened(true)
                  setDraftDescription(row.original.description)
                }}
                prefixIcon={<EditIcon />}
              />
            </BaseCell>
          )
        },
      },
    ],
    [
      getColumnWidth,
      updateAttribute,
      selectedRecord?.id,
      selectedRecord?.region,
      setAttributeToEdit,
      setIsEditAttributeModalOpened,
      setDraftDescription,
    ]
  )
}
